import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotifications, type Notification } from './useNotifications'
import { useCurrentUserProfile } from '../../hooks/useTracking'
import styles from './NotificationsBell.module.css'

export function NotificationsBell() {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const { data: profile } = useCurrentUserProfile()
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications()

  const telegramConnected = !!profile?.telegram_chat_id

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!wrapRef.current) return
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', handler)
    }
  }, [open])

  // Close on escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open])

  // Mark all as read when panel opens
  useEffect(() => {
    if (open && unreadCount > 0) markAllRead()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRowClick = (n: Notification) => {
    markRead(n.id)
    if (n.link) {
      setOpen(false)
      navigate(n.link)
    }
  }

  const title = unreadCount > 0
    ? `Notifications — ${unreadCount} unread`
    : 'Notifications'

  return (
    <div ref={wrapRef} className={styles.anchor}>
      <button
        type="button"
        className={`${styles.bell} ${open ? styles.bellActive : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label={title}
        title={title}
      >
        <i className={`bi ${unreadCount > 0 ? 'bi-bell-fill' : 'bi-bell'}`} />
        {unreadCount > 0 && (
          <span className={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Notifications">
          <div className={styles.head}>
            <div className={styles.headText}>
              <h3 className={styles.title}>Notifications</h3>
              <div className={styles.subtitle}>
                {telegramConnected ? (
                  <span className={styles.connectedTag}>
                    <i className="bi bi-telegram" /> Telegram connected
                  </span>
                ) : (
                  'Listing alerts will arrive here'
                )}
              </div>
            </div>
            <button
              className={styles.headBtn}
              onClick={() => setOpen(false)}
              aria-label="Close"
              title="Close"
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {notifications.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <i className={`bi ${telegramConnected ? 'bi-bell-slash' : 'bi-telegram'}`} />
              </div>
              <h4 className={styles.emptyTitle}>
                {telegramConnected ? 'No notifications yet' : 'Connect Telegram for alerts'}
              </h4>
              <p className={styles.emptyBody}>
                {telegramConnected
                  ? 'When a tracked release gets a new listing or hits your target price, it\'ll show up here and on Telegram.'
                  : 'Hook up Telegram in Settings to get pinged when tracked records hit a new listing or your target price.'}
              </p>
              {!telegramConnected && (
                <button
                  className={styles.connectBtn}
                  onClick={() => { setOpen(false); navigate('/settings') }}
                >
                  <i className="bi bi-telegram" />
                  Connect Telegram
                </button>
              )}
            </div>
          ) : (
            <div className={styles.list}>
              {notifications.map(n => (
                <button
                  key={n.id}
                  type="button"
                  className={`${styles.row} ${!n.read ? styles.unread : ''}`}
                  onClick={() => handleRowClick(n)}
                >
                  <span className={styles.rowIcon}>
                    <i className={`bi ${n.icon ?? 'bi-bell'}`} />
                  </span>
                  <span className={styles.rowText}>
                    <p className={styles.rowTitle}>{n.title}</p>
                    <p className={styles.rowBody}>{n.body}</p>
                    <p className={styles.rowTime}>{formatTime(n.createdAt)}</p>
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className={styles.foot}>
            <button
              className={styles.footLink}
              onClick={() => { setOpen(false); navigate('/tracking') }}
            >
              <i className="bi bi-eye" /> Tracked releases
            </button>
            <button
              className={styles.footLink}
              onClick={() => { setOpen(false); navigate('/settings') }}
            >
              <i className="bi bi-gear" /> Settings
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function formatTime(iso: string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diff = Math.max(0, Math.floor((now - then) / 1000))
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return new Date(iso).toLocaleDateString()
}
